import React, { useCallback, useEffect, useRef, useState } from "react";
import { getNotificationSummary, markNotificationsSeen, NotificationSummary } from "../api";

// NotificationBell (#2302): "N new in your filter" retention hook. Shows how
// many leads matching the user's saved buy boxes (or chosen counties) arrived
// since they last opened it. Opening the dropdown acknowledges them (POST
// /notifications/seen with the summary's asOf, so leads that landed after the
// count stay unread). Each row hands the buy box id to the parent, which opens
// the Leads tab with that box applied.
//
// Quiet by design: a 403 (not a paid plan) renders nothing; any other failure
// renders the bell with no badge and a plain message. It never throws into the
// header, and never fetches data beyond counts + box names.

export interface NotificationBellProps {
  /** Open the Leads tab, applying the given saved buy box when boxId is non-empty. */
  onOpenLeads: (boxId: string) => void;
}

type Status = "loading" | "ready" | "error" | "hidden";

// Refetch when the tab regains focus, but no more than once per interval.
const REFRESH_MIN_MS = 5 * 60 * 1000;

function badgeText(s: NotificationSummary): string {
  if (s.capped) return `${s.count}+`;
  return String(s.count);
}

const NotificationBell: React.FC<NotificationBellProps> = ({ onOpenLeads }) => {
  const [status, setStatus] = useState<Status>("loading");
  const [summary, setSummary] = useState<NotificationSummary | null>(null);
  const [open, setOpen] = useState(false);
  // Count acknowledged by opening the bell; badge stays clear until a fresh fetch.
  const [acked, setAcked] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const lastFetchRef = useRef(0);

  const load = useCallback(async () => {
    lastFetchRef.current = Date.now();
    try {
      const s = await getNotificationSummary();
      setSummary(s);
      setAcked(false);
      setStatus("ready");
    } catch (err) {
      const code = (err as { response?: { status?: number } })?.response?.status;
      setStatus(code === 403 ? "hidden" : "error");
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (status === "hidden") return;
    const onVisible = () => {
      if (document.visibilityState === "visible" && Date.now() - lastFetchRef.current > REFRESH_MIN_MS) {
        load();
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [status, load]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setOpen(false);
        buttonRef.current?.focus();
      }
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  if (status === "hidden") return null;

  const unread = status === "ready" && summary && !acked ? summary.count : 0;

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next && summary && summary.count > 0 && !acked) {
      setAcked(true);
      // Best effort: if this fails the badge simply returns on the next fetch.
      markNotificationsSeen(summary.asOf).catch(() => {});
    }
  };

  const label =
    unread > 0 && summary
      ? `Notifications: ${badgeText(summary)} new ${summary.count === 1 && !summary.capped ? "lead" : "leads"} in your filters`
      : "Notifications";

  const groups = summary?.groups.filter((g) => g.count > 0) ?? [];
  const showList = status === "ready" && summary && summary.count > 0;

  return (
    <div className="notif-bell" data-tour="notif-bell" ref={rootRef}>
      <button
        ref={buttonRef}
        type="button"
        className="notif-bell-btn"
        onClick={toggle}
        aria-label={label}
        aria-haspopup="true"
        aria-expanded={open}
        aria-controls="notif-bell-panel"
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
          <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
        </svg>
        {unread > 0 && summary && (
          <span className="notif-bell-badge" aria-hidden="true">
            {badgeText(summary)}
          </span>
        )}
      </button>
      {open && (
        <div className="notif-bell-panel" id="notif-bell-panel" role="region" aria-label="New leads in your filters">
          {status === "loading" && <p className="notif-bell-empty">Loading…</p>}
          {status === "error" && (
            <p className="notif-bell-empty">
              Couldn't load notifications.{" "}
              <button type="button" className="notif-bell-retry" onClick={load}>
                Try again
              </button>
            </p>
          )}
          {status === "ready" && !showList && (
            <p className="notif-bell-empty">
              {summary?.scope === "none"
                ? "Save a buy box in Leads to get a count of new matches here."
                : "You're all caught up. Nothing new in your filters."}
            </p>
          )}
          {showList && summary && (
            <>
              <p className="notif-bell-head">
                <span className="notif-bell-num">{badgeText(summary)}</span> new since you last checked
              </p>
              <ul className="notif-bell-list">
                {groups.map((g) => (
                  <li key={g.boxId || g.name}>
                    <button
                      type="button"
                      className="notif-bell-item"
                      onClick={() => {
                        setOpen(false);
                        onOpenLeads(g.boxId);
                      }}
                    >
                      <span className="notif-bell-item-name">{g.name}</span>
                      <span className="notif-bell-item-count">{g.count}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
